import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, ArrowLeft, RefreshCcw, Zap, Pause, Play,
  CheckCircle2, RotateCcw, ListChecks,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function Card({ title, icon, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 p-4 sm:p-5"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
    >
      <header className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm sm:text-base font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
          {title}
        </h3>
      </header>
      {children}
    </section>
  );
}

function Row({ label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-baseline justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-[12.5px] text-white/65">{label}</span>
      <span className="text-sm text-white tabular-nums">{value}</span>
    </div>
  );
}

export default function AdminGrowthEnginePage() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState({});
  const [policy, setPolicy] = useState({});
  const [digest, setDigest] = useState({});
  const [exceptionsBlock, setExceptionsBlock] = useState({});
  const [opps, setOpps] = useState({ opportunities: [] });

  useEffect(() => {
    if (!token) navigate('/admin/login?next=/admin/growth/engine', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Growth Engine — Admin (intern)';
    const m = document.createElement('meta');
    m.setAttribute('name', 'robots');
    m.setAttribute('content', 'noindex, nofollow, noarchive');
    m.setAttribute('data-admin-growth', '1');
    document.head.appendChild(m);
    return () => { document.title = prev; m.parentNode && m.parentNode.removeChild(m); };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setErr('');
    const hdr = { Authorization: `Bearer ${token}` };
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        fetch(`${API}/growth/engine/status`, { headers: hdr }),
        fetch(`${API}/growth/engine/policy`, { headers: hdr }),
        fetch(`${API}/growth/engine/digest/weekly`, { headers: hdr }),
        fetch(`${API}/growth/engine/exceptions`, { headers: hdr }),
        fetch(`${API}/growth/engine/modules/opportunities`, { headers: hdr }),
      ]);
      if ([r1, r2, r3, r4, r5].some(r => r.status === 401)) {
        navigate('/admin/login?next=/admin/growth/engine', { replace: true });
        return;
      }
      const [d1, d2, d3, d4, d5] = await Promise.all(
        [r1, r2, r3, r4, r5].map(r => r.json())
      );
      setStatus(d1); setPolicy(d2); setDigest(d3);
      setExceptionsBlock(d4); setOpps(d5);
    } catch {
      setErr('Kon Growth Engine niet laden.');
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePause = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const url = status.paused ? '/growth/engine/resume' : '/growth/engine/pause';
      await fetch(`${API}${url}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchAll();
    } finally { setBusy(false); }
  }, [token, status.paused, fetchAll]);

  const approveException = useCallback(async (id) => {
    setBusy(true);
    try {
      await fetch(`${API}/growth/engine/exceptions/${id}/approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      await fetchAll();
    } finally { setBusy(false); }
  }, [token, fetchAll]);

  const rejectException = useCallback(async (id) => {
    setBusy(true);
    try {
      await fetch(`${API}/growth/engine/exceptions/${id}/reject`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      await fetchAll();
    } finally { setBusy(false); }
  }, [token, fetchAll]);

  if (!token) return null;
  const locks = status.locks || {};
  const counts = status.counts || {};
  const queued = exceptionsBlock?.exceptions || [];

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}
      data-testid="admin-growth-engine-page"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 sm:py-10">
        <button
          type="button"
          onClick={() => navigate('/admin?app=owner')}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-emerald-300 mb-6"
          data-testid="admin-growth-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar admin
        </button>

        <div className="mb-2 text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
          Intern · alleen admin/owner · read-only data
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }} data-testid="admin-growth-title">
          Growth Engine
        </h1>
        <p className="text-sm text-white/65 leading-relaxed mb-6 max-w-prose">
          Veilige commerciële optimalisatie. Riskante wijzigingen landen in
          de owner-queue. Geen live Ads, mails, pricing, Stripe, consent of
          device-control via deze laag.
        </p>

        <div
          className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 mb-5 flex items-start gap-3"
          data-testid="admin-growth-locks-banner"
        >
          <AlertTriangle className="w-5 h-5 text-rose-300 mt-0.5 shrink-0" />
          <div className="text-sm text-rose-100">
            <strong>Veiligheidssloten actief.</strong>{' '}
            GOOGLE_ADS_MUTATION_ENABLED=false, REAL_EMAIL_SEND_ENABLED=false,
            pricing_lock, stripe_lock, consent_lock, GLOBAL_DEVICE_MUTATION_LOCK,
            AUTO_SAFE_ENABLED=false.
          </div>
        </div>

        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <button
            type="button"
            disabled={busy || loading}
            onClick={togglePause}
            data-testid="admin-growth-pause-btn"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold disabled:opacity-60 ${
              status.paused
                ? 'bg-emerald-500 text-emerald-950 border-emerald-300'
                : 'bg-white/5 text-white/85 border-white/15 hover:bg-white/10'
            }`}
          >
            {status.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            {status.paused ? 'Hervat engine' : 'Pauzeer engine'}
          </button>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            data-testid="admin-growth-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 bg-white/5 text-white/85 text-xs"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>

        {err && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100 mb-5" data-testid="admin-growth-err">
            {err}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <Card title="Status" icon={<Zap className="w-4 h-4 text-emerald-300" />} testId="card-growth-status">
            <Row label="Paused" value={String(status.paused ?? false)} testId="growth-paused" />
            <Row label="Autonomy level (default)" value={status.autonomy_level_default ?? '—'} testId="growth-autonomy" />
            <Row label="Max owner-beslissingen/week" value={status.max_owner_decisions_per_week ?? 3} testId="growth-max-owner" />
            <Row label="Owner workload target (min)" value={status.owner_workload_target_minutes ?? 10} testId="growth-workload" />
            <Row label="Mutation types" value={status.mutation_type_count ?? '—'} testId="growth-mutation-count" />
            <Row label="Applied" value={counts.applied ?? 0} testId="growth-applied" />
            <Row label="Queued (owner)" value={counts.queued_for_owner ?? 0} testId="growth-queued" />
            <Row label="Rolled back" value={counts.rolled_back ?? 0} testId="growth-rolled" />
            <Row label="Blocked" value={counts.blocked ?? 0} testId="growth-blocked" />
          </Card>

          <Card title="Veiligheidssloten" icon={<ShieldCheck className="w-4 h-4 text-emerald-300" />} testId="card-growth-locks">
            <Row label="Ads mutations enabled" value={String(locks.google_ads_mutation_enabled ?? false)} testId="lock-ads" />
            <Row label="Real e-mail send enabled" value={String(locks.real_email_send_enabled ?? false)} testId="lock-mail" />
            <Row label="Pricing lock" value={String(locks.pricing_lock ?? true)} testId="lock-pricing" />
            <Row label="Stripe lock" value={String(locks.stripe_lock ?? true)} testId="lock-stripe" />
            <Row label="Consent lock" value={String(locks.consent_lock ?? true)} testId="lock-consent" />
            <Row label="Device mutation lock" value={String(locks.global_device_mutation_lock ?? true)} testId="lock-device" />
            <Row label="Auto_safe enabled" value={String(locks.auto_safe_enabled ?? false)} testId="lock-auto-safe" />
          </Card>

          <Card title="Wekelijkse owner-digest" icon={<ListChecks className="w-4 h-4 text-emerald-300" />} testId="card-growth-digest">
            <Row label="Automatische wijzigingen (7d)" value={(digest.automatic_changes || []).length} testId="digest-applied" />
            <Row label="Rollbacks (7d)" value={(digest.rollbacks || []).length} testId="digest-rollbacks" />
            <Row label="Owner-beslissingen (max 3)" value={(digest.owner_decisions_needed || []).length} testId="digest-owner-decisions" />
            <Row label="Owner workload (min)" value={digest.owner_workload_estimate_minutes ?? 0} testId="digest-workload" />
            <Row label="Voorkomen wijzigingen (7d)" value={digest.prevented_total ?? 0} testId="digest-prevented" />
            <p className="text-[12px] text-emerald-200 mt-3" data-testid="digest-prevented-summary">
              {digest.prevented_summary_nl || `Deze week voorkwam de Growth Engine ${digest.prevented_total ?? 0} geblokkeerde/verboden wijzigingen.`}
            </p>
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              {digest.policy_note || 'Veilige wijzigingen lopen al automatisch.'}
            </p>
          </Card>

          <Card title="Owner exceptions" icon={<AlertTriangle className="w-4 h-4 text-amber-300" />} testId="card-growth-exceptions">
            {queued.length === 0 ? (
              <p className="text-[12.5px] text-white/55" data-testid="growth-exceptions-empty">Geen open beslissingen.</p>
            ) : (
              <ul className="space-y-2" data-testid="growth-exceptions-list">
                {queued.map((q) => (
                  <li key={q.audit_id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-[12px] text-white/85 mb-1">
                      <span className="font-semibold">{q.mutation_type}</span>{' · '}
                      <span className="text-white/55">{q.risk_level}</span>
                    </div>
                    <p className="text-[12px] text-white/70 mb-2">{q.rationale_nl}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => approveException(q.audit_id)}
                        disabled={busy}
                        data-testid={`growth-exception-approve-${q.audit_id}`}
                        className="px-2.5 py-1 rounded-md bg-emerald-500 text-emerald-950 text-[11.5px] font-semibold disabled:opacity-60"
                      >
                        <CheckCircle2 className="w-3 h-3 inline mr-1" /> Goedkeuren
                      </button>
                      <button
                        onClick={() => rejectException(q.audit_id)}
                        disabled={busy}
                        data-testid={`growth-exception-reject-${q.audit_id}`}
                        className="px-2.5 py-1 rounded-md border border-white/15 bg-white/5 text-white/85 text-[11.5px] disabled:opacity-60"
                      >
                        <RotateCcw className="w-3 h-3 inline mr-1" /> Afwijzen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card title="Gedetecteerde kansen (read-only)" icon={<Zap className="w-4 h-4 text-emerald-300" />} testId="card-growth-opps">
          {(opps.opportunities || []).length === 0 ? (
            <p className="text-[12.5px] text-white/55">Geen kansen gedetecteerd.</p>
          ) : (
            <ul className="space-y-1.5">
              {(opps.opportunities || []).slice(0, 10).map((o, i) => (
                <li key={i} className="text-[12px] text-white/85" data-testid="growth-opp-row">
                  <span className="font-semibold">{o.module}</span>{' · '}
                  <span className="text-white/55">{o.mutation_type}</span>{' — '}
                  <span className="text-white/70">{o.rationale_nl}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-prose">
          Mutation policy ({policy.count || 0} types) zorgt dat alleen low-risk wijzigingen
          automatisch worden uitgevoerd. Riskante wijzigingen komen in deze queue.
          Maximaal 3 owner-beslissingen per week, target 10 minuten.
        </p>
      </div>
    </div>
  );
}
